<template>
  <div class="asc-fleet-bar-chart">
    <asc-spinner-wrapper :loading="loading">
      <div v-if="!isEmpty">
        <asc-chart :options="getChartOptions"></asc-chart>
      </div>
      <asc-no-data
        v-if="isEmpty"
        class="asc-fleet-bar-chart--no-data"
      ></asc-no-data>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import AscSpinnerWrapper from '../atoms/AscSpinnerWrapper.vue'
import AscNoData from '../atoms/AscNoData.vue'

import AscChart from '../molecules/AscChart.vue'

import variables from '@/styles/_varsJs.scss'

import _merge from 'lodash/merge'

export default {
  name: 'AscFleetBarChart',

  components: {
    AscSpinnerWrapper,
    AscNoData,
    AscChart,
  },

  props: {
    tails: {
      type: Array,
      default: () => [],
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    seriesData: {
      type: Array,
      default: () => [],
    },
    selectedItem: {
      type: [String, Array],
      default: '',
    },
    type: {
      type: String,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    enableDrill: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      defaultOptions: {
        chart: {
          spacing: [16, 24, 16, 16],
          style: {
            fontFamily: variables.chartfontfamily,
          },
        },
        title: {
          text: null,
        },
        legend: {
          enabled: false,
        },
      },
      selectedValue: this.multiple ? [] : '',
    }
  },

  computed: {
    isEmpty() {
      return this.tails.length === 0
    },

    getChartHeight() {
      if (this.tails && this.tails.length > 0) {
        return this.tails.length > 15 ? this.tails.length * 20 : 350
      }

      return 350
    },

    getChartOptions() {
      const _that = this
      if (this.tails.length) {
        const series = this.buildSeries
        const chart = this.buildChart
        const xAxisOptions = this.buildXAxisOptions
        const yAxisOptions = this.buildYAxisOptions
        const tooltipOptions = this.buildTooltipOptions
        const plotOptions = this.buildPlotOptions
        const options = _merge(
          {
            ...this.defaultOptions,
            ...series,
            ...xAxisOptions,
            ...yAxisOptions,
            ...tooltipOptions,
            ...chart,
            ...plotOptions,
          },
          _that.options,
        )
        return options
      }
      return this.defaultOptions
    },

    buildPlotOptions() {
      const plotOptions = {
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: false,
            },
            borderWidth: 3,
            pointWidth: 12,
            states: {
              hover: {
                enabled: false,
              },
            },
            zones:
              this.type === 'healthFeature'
                ? [
                    {
                      value: 1,
                      color: '#53B8B3',
                    },
                    {
                      color: '#D20532',
                    },
                  ]
                : undefined,
          },
          series: {
            cursor: this.enableDrill ? 'pointer' : null,
            pointWidth: 8,
            dataSorting: {
              enabled: false,
            },
            stacking: 'normal',
            states: {
              hover: {
                enabled: false,
              },
              inactive: {
                opacity: 1,
              },
            },
          },
        },
      }
      return plotOptions
    },

    buildChart() {
      return {
        chart: {
          height: this.getChartHeight,
        },
      }
    },

    buildYAxisOptions() {
      const _that = this
      return {
        yAxis: {
          gridLineDashStyle: variables.chartgridlinestyle,
          gridLineColor: variables.gray400,
          stackLabels: {
            enabled: true,
          },
          labels: {
            style: {
              color: variables.primaryblack,
              fontSize: variables.fontsizesm,
            },
            formatter: function () {
              if (_that.type === 'healthFeature') {
                if (this.value === 1) {
                  return '1.0'
                }
                if (this.value !== 0 && this.value !== 2) {
                  return '-'
                }
              }
              return this.value
            },
          },
          title: {
            text: null,
          },
          max: _that.type === 'healthFeature' ? 2.1 : null,
          plotLines:
            _that.type === 'healthFeature'
              ? [
                  {
                    value: 1,
                    color: variables.warningbase,
                    dashStyle: 'shortdash',
                    width: 2,
                  },
                ]
              : [],
        },
      }
    },

    buildXAxisOptions() {
      const _that = this
      return {
        xAxis: {
          categories: this.getCategories,
          labels: {
            useHtml: true,
            formatter: function () {
              const isValidDrillDown = _that.isValidDrillDown(this)

              if (isValidDrillDown) {
                if (!_that.multiple) {
                  if (this.pos === 0 && _that.selectedValue === '') {
                    return `<span style="font-weight: bold;">${this.value}</span>`
                  }
                  if (this.value === _that.selectedValue) {
                    return `<span style="font-weight: bold;">${this.value}</span>`
                  }
                }

                if (_that.multiple) {
                  if (_that.selectedValue.length === 0) {
                    return `<span style="font-weight: bold;">${this.value}</span>`
                  }
                  if (!_that.selectedValue.includes(this.value)) {
                    return `<span style="font-weight: bold;">${this.value}</span>`
                  }
                }

                return this.value
              }

              if (!isValidDrillDown) {
                return `<span style="color: #d1d1d1;">${this.value}</span>`
              }
            },
            style: {
              color: variables.primaryblack,
              fontSize: variables.fontsizesm,
              width: '70px',
              textOverflow: 'ellipsis',
            },
          },
          title: {
            text: null,
          },
        },
      }
    },

    buildTooltipOptions() {
      const _that = this
      let tooltipOptions = {}
      if (this.tails.length) {
        tooltipOptions = {
          tooltip: {
            formatter: function () {
              if (_that.type === 'openMaintenance') {
                return !_that.isIssueUpdater
                  ? `${this.y} ${this.series.name} Open Issues`
                  : `${this.y} ${this.series.name} Mx Recs`
              } else if (_that.type === 'faultMsg') {
                return `${this.y} ${this.series.name}`
              } else if (_that.type === 'ataChapter') {
                return `${this.y} ${this.series.name}`
              } else if (_that.type === 'para') {
                return `${this.y} ${this.series.name}`
              } else if (_that.type === 'healthTail') {
                return `${this.series.name} for ${this.x} adds up to ${this.y}`
              } else if (_that.type === 'healthFeature') {
                return `${this.x}`
              }
            },
            distance: 0,
            backgroundColor: variables.primaryblack,
            style: {
              color: variables.primarywhite,
              fontSize: variables.fontsizesm,
              whiteSpace: 'break-spaces',
              width: '200px',
              textAlign: 'center',
            },
            borderWidth: 0,
            split: true,
            shared: false,
          },
        }
      }
      return tooltipOptions
    },

    buildSeries() {
      const _that = this
      return {
        series: this.seriesData?.map(item => {
          return {
            ...item,
            type: 'bar',
            point: {
              events: {
                click: function () {
                  if (_that.enableDrill) {
                    const selected = _that.tails
                      .filter(item => item.name === this.category)
                      .pop()

                    if (_that.multiple) {
                      if (_that.selectedValue.includes(selected.name)) {
                        const filteredItems = _that.selectedValue.filter(
                          item => item !== selected.name,
                        )
                        _that.selectedValue = filteredItems
                      } else {
                        const filteredItems = _that.selectedValue.filter(
                          item => item !== selected.name,
                        )
                        filteredItems.push(selected.name)
                        _that.selectedValue = filteredItems
                      }
                    } else {
                      _that.selectedValue = selected.name
                    }

                    _that.$emit(
                      'onSeriesClick',
                      _that.selectedValue,
                      _that.multiple,
                    )
                    const chart = this.series.chart
                    chart.selectedIndex = this.x
                    chart.redraw(false)
                  }
                },
              },
            },
          }
        }),
      }
    },

    getCategories() {
      if (this.tails.length > 0) {
        const categories = this.tails.map(item => item.name)
        return categories
      }
      return []
    },
  },

  watch: {
    selectedItem(newVal) {
      this.selectedItem = newVal
      this.selectedValue = newVal
    },
  },

  methods: {
    isValidDrillDown(point) {
      const position = point.pos
      const count = this.seriesData.reduce((total, item) => {
        const newTotal = total + item.data[position]
        return newTotal
      }, 0)

      return count > 0
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-bar-chart {
  height: 350px;
  overflow-y: auto;

  &--no-data {
    min-height: 350px;
  }
}
</style>
